import React, { useState, useEffect, useRef } from "react";

import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";

import { OnboardingSelectorOptions } from "libLegacy/api/onboarding/onboardingModels";
import {
  createEnergyOperatorCredentials,
  fetchEnergyOperatorValues,
} from "libLegacy/api/onboarding/onboarding";

import { BlockStandard } from "components/admin/BlockStandard";
import { CustomSelect } from "components/onboarding/customSelect";
import { SolutionsFrame } from "components/solutions/SolutionsFrame";
import { useToken } from "contexts/UserContext";
import { useSetError, useErrorInfo } from "contexts/ErrorContext";
import { CustomCodes } from "contexts/ErrorContextData";

const FRAME_DATA = {
  title: "Monitorar meu sistema solar",
  buttonText: "Continuar",
  nextScreen: "/solutions/prosumer-credentials-end",
  imgSrc: "/static/img/solutions/question.png",
  imgAlt: "",
};

export const ProsumerBillingCredentials = ({ history, location }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hidePassword, setHidePassword] = useState(true);
  const token = useToken();
  const setGlobalError = useSetError();
  const errorInfo = useErrorInfo();
  const hasFormError = errorInfo?.code === CustomCodes.INCOMPLETE_FORM;

  const [operators, setOperators] =
    useState<Array<OnboardingSelectorOptions>>();
  const [chosenOperatorId, setChosenOperatorId] =
    useState<OnboardingSelectorOptions["id"]>();

  const refEmail = useRef<HTMLInputElement>(null);
  const refPassword = useRef<HTMLInputElement>(null);
  const skipTo = location.state?.returnToDashboard
    ? "/admin/monitoring"
    : "/solutions/prosumer-credentials-end";

  useEffect(() => {
    const loadSelectData = async (token) => {
      try {
        const data = await fetchEnergyOperatorValues(token);
        setOperators(data);
      } catch (error) {
        // @ts-expect-error
        setGlobalError(error.response?.data?.error);
      } finally {
        setIsLoading(false);
      }
    };
    loadSelectData(token);
  }, [token, setGlobalError]);

  const handleClick = async () => {
    const email = refEmail.current?.value;
    const password = refPassword.current?.value;
    const desiredPlatform = operators?.find(
      ({ id }) => id === chosenOperatorId
    );

    if (email && password && desiredPlatform) {
      createCredentials({
        email,
        password,
        desiredPlatform,
        id: chosenOperatorId,
      });
    } else {
      setGlobalError(CustomCodes.INCOMPLETE_FORM);
    }
  };

  const createCredentials = async (formData) => {
    setIsLoading(true);
    try {
      await createEnergyOperatorCredentials(token, formData);
      handleNextNavigation();
    } catch (error) {
      // @ts-expect-error
      setGlobalError(error.response?.data?.error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNextNavigation = () => {
    const returnToDashboard = location.state?.returnToDashboard;

    if (returnToDashboard) {
      history.push("");
    } else {
      history.push(FRAME_DATA.nextScreen);
    }
  };

  const toggleVisibility = () => {
    setHidePassword((prev) => !prev);
  };

  const passwordIcon = (
    <InputAdornment position="end">
      <IconButton onClick={toggleVisibility}>
        {hidePassword ? <Visibility /> : <VisibilityOff />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <SolutionsFrame
      {...FRAME_DATA}
      onButtonClick={handleClick}
      isLoading={isLoading}
      skipTo={skipTo}
    >
      <BlockStandard hideDivider>
        <Typography variant="h2">
          Insira seus dados de acesso ao site da concessionária de energia
        </Typography>
        <Typography>
          Para verificarmos se a concessionária de energia está computando
          corretamente seus créditos de energia precisamos ter acesso às suas
          faturas
        </Typography>
      </BlockStandard>

      {operators && (
        <BlockStandard hideDivider>
          <CustomSelect
            value={chosenOperatorId}
            onChange={({ target: { value } }) => {
              setChosenOperatorId(value);
            }}
            options={operators}
          />
        </BlockStandard>
      )}

      {chosenOperatorId && (
        <BlockStandard hideDivider>
          <TextField
            required
            inputRef={refEmail}
            label="Seu login"
            variant="outlined"
            error={hasFormError && !refEmail.current?.value}
          />
          <TextField
            required
            inputRef={refPassword}
            InputProps={{ endAdornment: passwordIcon }}
            label="Senha de acesso"
            type={hidePassword ? "password" : "text"}
            variant="outlined"
            error={hasFormError && !refPassword.current?.value}
          />
        </BlockStandard>
      )}
    </SolutionsFrame>
  );
};
